import { type FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";

import type { AddressCustomShippingFieldValue } from "@jti/commerce-types";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { newrelic } from "@jti/fe-clients";
import { useTranslations } from "@jti/ui";

import { CustomShippingOptionWidget } from "@checkout-v2/features/CustomShippingOptionWidget";
import type { ShippingFormFieldsConfig } from "@checkout-v2/forms";
import type { FormFieldValues } from "@checkout-v2/forms/forms.types";
import { useOnShippingAddressChange } from "@checkout-v2/forms/ShippingForm/renderer/utils/useOnShippingAddressChange";
import { useSetShippingAddressOnCart } from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";
import { isExternalCarrierAddress } from "@checkout-v2/shared/type-guards/isExternalCarrierAddress";
import { getShippingAddressErrorMessage } from "@checkout-v2/shared/utils";
import { findCustomShippingMethod } from "@checkout-v2/shared/utils/findCustomShippingMethod";

export type AddressCustomShippingFieldProps = {
  formFieldsConfig: ShippingFormFieldsConfig;
  selectedShippingMethodCode: string;
};

export const AddressCustomShippingField: FC<AddressCustomShippingFieldProps> = ({
  formFieldsConfig,
  selectedShippingMethodCode,
}) => {
  const { __ } = useTranslations();
  const fieldConfig = formFieldsConfig.addressCustomShipping;
  const form = useFormContext<FormFieldValues<{ [fieldConfig.name]: AddressCustomShippingFieldValue }>>();
  const { onShippingAddressChange } = useOnShippingAddressChange(formFieldsConfig);
  const { setShippingAddressOnCartState } = useSetShippingAddressOnCart();

  const shippingAddressFormValidationErrorMessage = form.formState.errors.addressCustomShipping?.message;
  const shippingAddressServerErrorMessage = getShippingAddressErrorMessage(setShippingAddressOnCartState.error, __);
  const shippingAddressErrorMessage = shippingAddressFormValidationErrorMessage || shippingAddressServerErrorMessage;

  const selectedShippingMethod = findCustomShippingMethod(fieldConfig.shippingMethods, selectedShippingMethodCode);

  const onAddressChange = async (address: unknown) => {
    form.clearErrors(fieldConfig.name);

    const newAddressValue = isExternalCarrierAddress(address) ? address : null;

    form.setValue(fieldConfig.name, newAddressValue);

    const isValid = await form.trigger(fieldConfig.name);

    if (!isValid) {
      return;
    }

    if (!address || !newAddressValue) return;

    await onShippingAddressChange(newAddressValue);
  };

  useEffect(function setShippingAddressOnCartOnInitialMount() {
    const selectedAddress = form.getValues(fieldConfig.name);

    if (!selectedShippingMethod || !selectedAddress || !isExternalCarrierAddress(selectedAddress)) {
      return;
    }

    onShippingAddressChange(selectedAddress).catch((error) => {
      newrelic.noticeError(error as Error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!selectedShippingMethod) {
    newrelic.noticeError(
      new Error(
        `AddressCustomShippingField: couldn't find shipping method for selected option: ${selectedShippingMethodCode}. Check shipping methods configuration.`
      )
    );
    return null;
  }

  return (
    <>
      {shippingAddressErrorMessage && (
        <AlertBanner tone="error" data-testid="widget-error" description={shippingAddressErrorMessage} />
      )}

      <Controller
        control={form.control}
        name={fieldConfig.name}
        rules={{
          required: fieldConfig.registerOptions?.required,
          validate: fieldConfig.registerOptions?.validate,
        }}
        render={({ field }) => (
          <CustomShippingOptionWidget
            key={selectedShippingMethod.widgetTagName}
            webComponentTagName={selectedShippingMethod.widgetTagName}
            widgetUrl={selectedShippingMethod.widgetUrl}
            variant={selectedShippingMethod.widgetVariant}
            selectedAddress={field.value}
            onSelectAddress={onAddressChange}
            addressValidationError={shippingAddressErrorMessage}
          />
        )}
      />
    </>
  );
};
